import React, { useState, useEffect, useRef } from "react";
import { FiArrowLeft, FiSearch, FiFolder, FiCalendar, FiX, FiDownload, FiFile, FiFileText } from "react-icons/fi";
import { FaRegFilePdf, FaFileImage } from "react-icons/fa";
import { Document, Page, pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  limit,
  doc,
  getDocs,
  getDoc,
  runTransaction
} from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import { validateMessage } from "../../utils/profanityFilter";
import ProfanityModal from "../../components/usercomponents/ProfanityModal";
import "./staffcss/PeerChatform.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ESCALATION_NOTICE_TEXT =
  "Your concern has been forwarded to a Medical Professional for further assistance. Please wait for their response.";

const PeerChatForm = ({ chatId, onBackToQueue, readOnly = false }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState("active");
  const [isEscalated, setIsEscalated] = useState(false);
  const [escalationStatus, setEscalationStatus] = useState(null);
  const [peerEducatorName, setPeerEducatorName] = useState("");
  const [peerEducatorData, setPeerEducatorData] = useState(null);
  const [ticketNumber, setTicketNumber] = useState("");
  const [inquiryCreatedAt, setInquiryCreatedAt] = useState(null);
  const [inquiryTopic, setInquiryTopic] = useState("");

  // Transfer state — read from Firestore
  const [isTransferred, setIsTransferred] = useState(false);
  const [transferData, setTransferData] = useState(null); // { transferredBy, transferredAt, transferStatus, transferForm }
  const [transferredByName, setTransferredByName] = useState("");
  const [showProfanityModal, setShowProfanityModal] = useState(false);
  
  // Ref for auto-scrolling messages
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  // References panel state
  const [activeTab, setActiveTab] = useState("ref_resources");
  
  // Escalate modal state
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalationForm, setEscalationForm] = useState({
    topic: "",
    reason: "",
    notes: ""
  });
  const [escalationErrors, setEscalationErrors] = useState({
    topic: false,
    reason: false,
    notes: false
  });
  

  
  // Next Steps state
  const [nextSteps, setNextSteps] = useState({
    context: "",
    unresolvedChecklist: [],
    unresolvedAdditional: "",
    recommendedActions: [],
    customRecommendation: "",
    instructions: ""
  });
  
  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Resources state
  const [resources, setResources] = useState([]);
  const [resourceSearch, setResourceSearch] = useState("");
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);

  const peerEducatorId = auth.currentUser?.uid;

  // Fetch peer educator data (cache for reuse)
  useEffect(() => {
    const fetchPeerEducatorData = async () => {
      try {
        if (peerEducatorId) {
          const userDoc = await getDoc(doc(db, 'users', peerEducatorId));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setPeerEducatorData(data);
            const fullName = data.fullName || "";
            const firstName = fullName.split(' ')[0] || "Peer Educator";
            setPeerEducatorName(firstName);
          } else {
            setPeerEducatorName("Peer Educator");
          }
        }
      } catch (error) {
        console.error('Error fetching peer educator data:', error);
        setPeerEducatorName("Peer Educator");
      }
    };

    fetchPeerEducatorData();
  }, [peerEducatorId]);

  // Fetch resources once on mount
  useEffect(() => {
    const fetchResources = async () => {
      try {
        const q = query(
          collection(db, "resources"), 
          where("status", "!=", "Archived"),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        const resourcesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setResources(resourcesData);
      } catch (error) {
        console.error("Error fetching resources:", error);
      }
    };

    fetchResources();
  }, []);

  // Real-time listener for messages and chat status - OPTIMIZED
  useEffect(() => {
    if (!chatId) return;

    let mounted = true;
    setLoading(true);
    
    const chatRef = doc(db, "chats", chatId);
    const chatUnsubscribe = onSnapshot(chatRef, 
      (docSnapshot) => {
        if (!mounted) return;
        
        if (docSnapshot.exists()) {
          const chatData = docSnapshot.data();
          setChatStatus(chatData.status || "active");
          setTicketNumber(chatData.ticketNumber || "");
          setIsEscalated(chatData.escalatedChat === true);
          setEscalationStatus(chatData.escalationStatus || null);
          setInquiryCreatedAt(chatData.createdAt);
          setInquiryTopic(chatData.inquiryTopic || "");

          // Transfer detection
          const transferred = !!chatData.transferStatus; // any transferStatus means it was transferred
          setIsTransferred(transferred);
          if (transferred) {
            setTransferData({
              transferredBy: chatData.transferredBy || null,
              transferredAt: chatData.transferredAt || null,
              transferStatus: chatData.transferStatus || null,
              transferForm: chatData.transferForm || null,
            });
            // Fetch the transferring PE's name (only once per uid)
            if (chatData.transferredBy) {
              getDoc(doc(db, "users", chatData.transferredBy))
                .then((snap) => {
                  if (snap.exists()) {
                    const d = snap.data();
                    setTransferredByName(
                      d.fullName || `${d.firstName || ""} ${d.lastName || ""}`.trim() || "Unknown Peer Educator"
                    );
                  } else {
                    setTransferredByName("Unknown Peer Educator");
                  }
                })
                .catch(() => setTransferredByName("Unknown Peer Educator"));
            }
          }
        }
      },
      (error) => {
        console.error("Error fetching chat status:", error);
      }
    );
    
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(
      messagesRef,
      orderBy("order", "asc"),
      orderBy("createdAt", "asc"),
      limit(200) // Add limit
    );

    const messagesUnsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        if (!mounted) return;
        
        const messagesData = [];
        querySnapshot.forEach((doc) => {
          messagesData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setMessages(messagesData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching messages:", error);
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      chatUnsubscribe();
      messagesUnsubscribe();
    };
  }, [chatId]);

  // Handle ending chat
  const handleEndChat = async () => {
    if (!chatId) return;

    try {
      const chatRef = doc(db, "chats", chatId);
      const chatSnapshot = await getDoc(chatRef);
      
      if (!chatSnapshot.exists()) {
        alert("Chat not found.");
        return;
      }

      await updateDoc(chatRef, {
        status: "resolved",
        resolvedAt: serverTimestamp(),
        resolvedBy: peerEducatorId
      });
    } catch (error) {
      console.error("Error ending chat:", error);
      alert("Failed to end chat. Please try again.");
    }
  };

  // Handle escalate button click
  const handleEscalateClick = () => {
    setEscalationForm({
      topic: inquiryTopic || "",
      reason: "",
      notes: ""
    });
    setEscalationErrors({
      topic: false,
      reason: false,
      notes: false
    });
    setShowEscalateModal(true);
  };

  // Handle escalate form change
  const handleEscalationFormChange = (field, value) => {
    setEscalationForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (value.trim()) {
      setEscalationErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Handle escalate submit
  const handleEscalateSubmit = async () => {
    // Validate required fields
    const errors = {
      topic: !escalationForm.topic.trim(),
      reason: !escalationForm.reason.trim(),
      notes: !escalationForm.notes.trim()
    };
    
    setEscalationErrors(errors);
    
    if (errors.topic || errors.reason || errors.notes) {
      return; // Don't submit if there are errors
    }

    // Validate for profanity
    const topicValidation = validateMessage(escalationForm.topic);
    if (!topicValidation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    const reasonValidation = validateMessage(escalationForm.reason);
    if (!reasonValidation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    const notesValidation = validateMessage(escalationForm.notes);
    if (!notesValidation.isValid) {
      setShowProfanityModal(true);
      return;
    }
    
    try {
      // Save to escalated chats collection
      await addDoc(collection(db, "escalatedChats"), {
        ticketNumber: ticketNumber,
        escalationTopic: escalationForm.topic,
        escalationReason: escalationForm.reason,
        escalationNotes: escalationForm.notes,
        peerEducatorId: peerEducatorId,
        chatId: chatId,
        escalationStatus: "pending-review",
        createdAt: serverTimestamp()
      });

      // Tag chat as escalated and add escalation notice in a transaction
      const chatRef = doc(db, "chats", chatId);
      const messagesRef = collection(db, "chats", chatId, "messages");
      
      await runTransaction(db, async (transaction) => {
        const chatDoc = await transaction.get(chatRef);
        if (!chatDoc.exists()) {
          throw new Error("Chat document does not exist!");
        }
        
        const chatData = chatDoc.data();
        const currentOrder = chatData.nextMessageOrder || 0;
        
        transaction.update(chatRef, {
          escalatedChat: true,
          escalatedAt: serverTimestamp(),
          escalationStatus: "pending-review",
          status: "escalated",
          nextMessageOrder: currentOrder + 1
        });
        
        const escalationNoticeRef = doc(messagesRef, `escalation-notice-${chatId}`);
        transaction.set(escalationNoticeRef, {
          senderId: peerEducatorId || auth.currentUser?.uid || "system",
          senderRole: "system",
          text: ESCALATION_NOTICE_TEXT,
          createdAt: serverTimestamp(),
          order: currentOrder,
          isEscalationNotice: true
        });
      });

      // Decrement activeChats count for the peer educator using cached data
      const userDocRef = doc(db, "users", peerEducatorId);
      const currentActiveChats = peerEducatorData?.activeChats || 0;
      const newActiveChats = Math.max(0, currentActiveChats - 1);
      await updateDoc(userDocRef, {
        activeChats: newActiveChats
      });
      
      // Update cached data
      if (peerEducatorData) {
        setPeerEducatorData(prev => ({
          ...prev,
          activeChats: newActiveChats
        }));
      }

      setShowEscalateModal(false);
      
      if (onBackToQueue) {
        onBackToQueue();
      }
    } catch (error) {
      console.error("Error escalating chat:", error);
      alert("Failed to escalate chat. Please try again.");
    }
  };

  // Handle escalate cancel
  const handleEscalateCancel = () => {
    setShowEscalateModal(false);
  };

  // Handle sending message
  const handleSendMessage = async (text = newMessage) => {
    if (!text.trim() || !peerEducatorId || chatStatus === "resolved" || isEscalated) {
      if (chatStatus === "resolved") {
        alert("This chat has been resolved. No more messages can be sent.");
        return;
      }
      if (isEscalated) {
        alert(
          escalationStatus === "resolved"
            ? "This chat has been resolved by a Medical Professional. No more messages can be sent."
            : "This chat has been escalated to a Medical Professional. No more messages can be sent."
        );
        return;
      }
      return;
    }

    // Validate message for profanity
    const validation = validateMessage(text);
    if (!validation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    setIsTyping(true);

    try {
      const chatRef = doc(db, "chats", chatId);
      const messagesRef = collection(db, "chats", chatId, "messages");
      
      await runTransaction(db, async (transaction) => {
        const chatDoc = await transaction.get(chatRef);
        if (!chatDoc.exists()) {
          throw new Error("Chat document does not exist!");
        }
        
        const chatData = chatDoc.data();
        
        // Calculate next message order - use nextMessageOrder if available, otherwise calculate from existing messages
        let currentOrder = chatData.nextMessageOrder || 0;
        
        // If nextMessageOrder is 0 or missing, query existing messages to get the max order
        if (currentOrder === 0) {
          const messagesQuery = query(
            collection(db, "chats", chatId, "messages"),
            orderBy("order", "desc"),
            limit(1)
          );
          const messagesSnapshot = await getDocs(messagesQuery);
          if (!messagesSnapshot.empty) {
            const lastMessage = messagesSnapshot.docs[0];
            const lastOrder = lastMessage.data().order || 0;
            currentOrder = lastOrder + 1;
          }
        }
        
        const messageRef = doc(messagesRef);
        transaction.set(messageRef, {
          senderId: peerEducatorId,
          senderRole: "peer",
          text: text.trim(),
          createdAt: serverTimestamp(),
          order: currentOrder
        });
        
        transaction.update(chatRef, {
          lastMessage: text.trim(),
          updatedAt: serverTimestamp(),
          peerLastActivityAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          lastMessageSender: "peer",
          nextMessageOrder: currentOrder + 1
        });
      });

      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsTyping(false);
    }
  };

  // Open resource modal
  const openResourceModal = (resource) => {
    setSelectedResource(resource);
    setShowResourceModal(true);
    setPdfPageNumber(1);
    setPdfNumPages(null);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
  };

  const nextPage = () => {
    setPdfPageNumber((prevPageNumber) =>
      prevPageNumber + 1 > pdfNumPages ? pdfNumPages : prevPageNumber + 1
    );
  };

  const prevPage = () => {
    setPdfPageNumber((prevPageNumber) =>
      prevPageNumber - 1 < 1 ? 1 : prevPageNumber - 1
    );
  };



  // Filter resources
  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(resourceSearch.toLowerCase()) ||
    r.category.toLowerCase().includes(resourceSearch.toLowerCase())
  );

  // Calculate progress for Next Steps tab
  const calculateProgress = () => {
    let completed = 0;
    const total = 4;

    // 1. Conversation Context (Required)
    if (nextSteps.context.trim()) completed++;

    // 2. Unresolved Concerns (Optional) - always count as completed since it's optional
    completed++;

    // 3. Recommended Next Actions (Required)
    if (nextSteps.recommendedActions.length > 0 || nextSteps.customRecommendation.trim()) completed++;

    // 4. Instructions for Next Peer Educator (Optional) - always count as completed since it's optional
    completed++;

    return { completed, total };
  };

  const { completed, total } = calculateProgress();

  // Unresolved concerns checklist options
  const unresolvedOptions = [
    { id: "stopped_responding", label: "User stopped responding" },
    { id: "medical_referral", label: "Needs medical referral" },
    { id: "follow_up_needed", label: "Follow-up needed" },
    { id: "emotional_distress_unresolved", label: "Emotional distress unresolved" },
    { id: "appointment_not_completed", label: "Appointment not completed" }
  ];

  // Recommended actions options
  const recommendedOptions = [
    { id: "encourage_follow_up", label: "Encourage follow-up appointment" },
    { id: "provide_resources", label: "Provide educational resources" },
    { id: "monitor_distress", label: "Monitor emotional distress" },
    { id: "continue_conversation", label: "Continue counseling conversation" },
    { id: "custom", label: "Custom Recommendation" }
  ];

  // Handle Save Next Steps
  const handleSaveNextSteps = async () => {
    // Validate required sections
    if (!nextSteps.context.trim()) {
      setErrorMessage("Please fill in the Conversation Context section (required).");
      setShowErrorModal(true);
      return;
    }

    if (!nextSteps.recommendedActions.length && !nextSteps.customRecommendation.trim()) {
      setErrorMessage("Please select at least one Recommended Next Action or add a custom recommendation (required).");
      setShowErrorModal(true);
      return;
    }

    // Validate for profanity
    const contextValidation = validateMessage(nextSteps.context);
    if (!contextValidation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    if (nextSteps.customRecommendation.trim()) {
      const customValidation = validateMessage(nextSteps.customRecommendation);
      if (!customValidation.isValid) {
        setShowProfanityModal(true);
        return;
      }
    }

    if (nextSteps.unresolvedAdditional.trim()) {
      const unresolvedValidation = validateMessage(nextSteps.unresolvedAdditional);
      if (!unresolvedValidation.isValid) {
        setShowProfanityModal(true);
        return;
      }
    }

    if (nextSteps.instructions.trim()) {
      const instructionsValidation = validateMessage(nextSteps.instructions);
      if (!instructionsValidation.isValid) {
        setShowProfanityModal(true);
        return;
      }
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Build unresolved concerns object
      const unresolvedConcerns = {
        checklist: nextSteps.unresolvedChecklist,
        additional: nextSteps.unresolvedAdditional
      };

      // Build recommended next actions object
      const recommendedNextActions = {
        actions: nextSteps.recommendedActions,
        custom: nextSteps.customRecommendation
      };

      // Update chat document with transferForm and mark as transferring
      const chatRef = doc(db, "chats", chatId);
      await updateDoc(chatRef, {
        transferForm: {
          conversationContext: nextSteps.context,
          recommendedNextActions,
          unresolvedConcerns,
          additionalUnresolvedConcerns: nextSteps.unresolvedAdditional,
          instructionsNextPeer: nextSteps.instructions
        },
        peerEducatorId: null,
        assignedTo: null,
        assignedAt: null,
        transferredBy: peerEducatorId,
        transferredAt: serverTimestamp(),
        transferStatus: "pending"
      });

      // Decrement activeChats count for the peer educator using cached data
      const userDocRef = doc(db, "users", peerEducatorId);
      const currentActiveChats = peerEducatorData?.activeChats || 0;
      const newActiveChats = Math.max(0, currentActiveChats - 1);
      await updateDoc(userDocRef, {
        activeChats: newActiveChats
      });
      
      // Update cached data
      if (peerEducatorData) {
        setPeerEducatorData(prev => ({
          ...prev,
          activeChats: newActiveChats
        }));
      }

      // Show success feedback
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);

      // Navigate back to queue after 2 seconds
      setTimeout(() => {
        if (onBackToQueue) {
          onBackToQueue();
        }
      }, 2000);
    } catch (error) {
      console.error("Error saving next steps:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pcf-layout">
      {/* Left Column - Conversation Panel */}
      <div className="pcf-left-column">
        {/* Header */}
        <div className="pcf-header">
          <div className="pcf-header-left">
            {onBackToQueue && (
              <button className="pcf-back-btn" onClick={onBackToQueue}>
                <FiArrowLeft />
              </button>
            )}
            <div style={{ textAlign: "left" }}>
              <h3>Peer Educator Chat</h3>
              <div className="pcf-chat-id">Chat ID: {chatId}</div>
            </div>
          </div>
          {chatStatus !== "resolved" && !isEscalated && !readOnly && (
            <div style={{ display: "flex", gap: "8px" }}>
              <button className="pcf-escalate-btn" onClick={handleEscalateClick}>
                Escalate
              </button>
              <button className="pcf-end-chat-btn" onClick={handleEndChat}>
                End Chat
              </button>
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className="pcf-messages-container">
          {loading ? (
            <div className="pcf-loading">
              <div className="pcf-spinner"></div>
              <p>Loading messages...</p>
            </div>
          ) : (
            <div className="pcf-messages">
              {messages.map((message, index) => {
                let timestampToUse = message.createdAt;
                
                if (index === 0 && message.senderRole === "user" && inquiryCreatedAt) {
                  timestampToUse = inquiryCreatedAt;
                }
                
                const isSystem = message.senderRole === "system";
                const isUser = message.senderRole === "user";
                const isMedicalProfessional =
                  message.senderRole === "medical professional" || message.isMedicalFeedback;
                
                return (
                  <div 
                    key={message.id} 
                    className={`pcf-message ${isUser ? "pcf-user" : "pcf-educator"}`}
                  >
                    <div className="pcf-message-content">
                      <div className="pcf-message-role">
                        {isSystem 
                          ? `Peer Educator - (${peerEducatorName || "Peer Educator"})`
                          : isUser 
                          ? `User - ${ticketNumber ? `${ticketNumber}` : "No Ticket"}`
                          : isMedicalProfessional
                          ? "Medical Professional"
                          : `Peer Educator - (${peerEducatorName || "Peer Educator"})`
                        }
                      </div>
                      <div className="pcf-message-text">
                        {message.text}
                      </div>
                      <div className="pcf-message-time">
                        {timestampToUse?.toDate()?.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="pcf-input-container">
          {chatStatus === "resolved" ? (
            <div className="pcf-chat-resolved-notice">
              <p>This chat has been resolved. No more messages can be sent.</p>
            </div>
          ) : isEscalated && escalationStatus === "resolved" ? (
            <div className="pcf-chat-resolved-notice">
              <p>This chat has been resolved by a Medical Professional. No more messages can be sent.</p>
            </div>
          ) : isEscalated ? (
            <div className="pcf-chat-resolved-notice">
              <p>This chat has been escalated to a Medical Professional. No more messages can be sent.</p>
            </div>
          ) : (
            <>
              <textarea
                className="pcf-message-input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={readOnly ? "This chat is in read-only mode" : "Type your message..."}
                rows={3}
                disabled={readOnly}
              />
              <button
                className={`pcf-send-btn ${isTyping ? 'typing' : ''}`}
                onClick={() => handleSendMessage()}
                disabled={!newMessage.trim() || isTyping || readOnly}
              >
                {isTyping ? '...' : 'Send'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Right Column - References Panel - Hide for escalated chats or read-only mode */}
      {!isEscalated && !readOnly && (
      <div className="pcf-right-column">
        <div className="pcf-references-panel">
          {/* Tabs */}
          <div className="pcf-tabs">
            <button 
              className={`pcf-tab ${activeTab === 'ref_resources' ? 'active' : ''}`}
              onClick={() => setActiveTab('ref_resources')}
            >
              <FiFolder /> Resources
            </button>
            <button 
              className={`pcf-tab ${activeTab === 'next_steps' ? 'active' : ''}`}
              onClick={() => setActiveTab('next_steps')}
            >
              <FiCalendar /> {isTransferred ? 'Transfer Details' : 'Transfer'}
            </button>
          </div>

          {/* Tab Content */}
          <div className="pcf-tab-content">
            {activeTab === 'ref_resources' && (
              <div className="pcf-resources-tab">
                <div className="pcf-resource-search">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search by category or title..."
                    value={resourceSearch}
                    onChange={(e) => setResourceSearch(e.target.value)}
                  />
                </div>
                <div className="pcf-resources-grid">
                  {filteredResources.map(resource => (
                    <div
                      key={resource.id}
                      className="pcf-resource-card"
                      onClick={() => openResourceModal(resource)}
                    >
                      <div className="pcf-resource-icon">
                        {resource.type === 'pdf' ? <FaRegFilePdf /> : <FaFileImage />}
                      </div>
                      <div className="pcf-resource-content">
                        <h4>{resource.title}</h4>
                        <div className="pcf-resource-meta">
                          <span className="pcf-resource-category">{resource.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}



            {activeTab === 'next_steps' && (
              <div className="pcf-nextsteps-tab">
                {isTransferred ? (
                  /* ── TRANSFER DETAILS (read-only) ─────────────────── */
                  <div className="pcf-transfer-details">
                    {/* Header */}
                    <div className="pcf-transfer-details-header">
                      <div className="pcf-transfer-details-badge">
                        <span className="pcf-transfer-details-badge-dot" />
                        Already Transferred
                      </div>
                      <p className="pcf-transfer-details-note">
                        This chat has already been transferred. No further transfers are allowed.
                      </p>
                    </div>

                    {/* Meta */}
                    <div className="pcf-transfer-details-meta">
                      <div className="pcf-transfer-details-meta-row">
                        <span className="pcf-transfer-details-meta-label">Transferred by</span>
                        <span className="pcf-transfer-details-meta-value">
                          {transferredByName || "Loading…"}
                        </span>
                      </div>
                      <div className="pcf-transfer-details-meta-row">
                        <span className="pcf-transfer-details-meta-label">Transferred at</span>
                        <span className="pcf-transfer-details-meta-value">
                          {transferData?.transferredAt
                            ? (transferData.transferredAt.toDate?.() || new Date(transferData.transferredAt))
                                .toLocaleString("en-US", {
                                  month: "short", day: "numeric", year: "numeric",
                                  hour: "numeric", minute: "2-digit", hour12: true
                                })
                            : "—"}
                        </span>
                      </div>
                      <div className="pcf-transfer-details-meta-row">
                        <span className="pcf-transfer-details-meta-label">Transfer status</span>
                        <span className={`pcf-transfer-details-status-pill pcf-transfer-details-status-${transferData?.transferStatus || "pending"}`}>
                          {transferData?.transferStatus === "assigned"
                            ? "Assigned to new educator"
                            : transferData?.transferStatus === "pending"
                            ? "Pending assignment"
                            : transferData?.transferStatus || "—"}
                        </span>
                      </div>
                    </div>

                    {/* Transfer Form Data */}
                    {transferData?.transferForm && (() => {
                      const tf = transferData.transferForm;
                      const actions = tf.recommendedNextActions?.actions || [];
                      const customAction = tf.recommendedNextActions?.custom || "";
                      const checklist = tf.unresolvedConcerns?.checklist || [];
                      const additionalConcerns =
                        tf.unresolvedConcerns?.additional ||
                        tf.additionalUnresolvedConcerns || "";

                      return (
                        <>
                          {/* Conversation Context */}
                          <div className="pcf-nextsteps-card">
                            <div className="pcf-nextsteps-card-header">
                              <div className="pcf-nextsteps-step-indicator">
                                <span className="pcf-nextsteps-step-number">1</span>
                              </div>
                              <div className="pcf-nextsteps-card-title">
                                Conversation Context
                              </div>
                            </div>
                            <div className="pcf-nextsteps-card-content">
                              {tf.conversationContext ? (
                                <p className="pcf-transfer-details-text">{tf.conversationContext}</p>
                              ) : (
                                <p className="pcf-transfer-details-empty">Not provided</p>
                              )}
                            </div>
                          </div>

                          {/* Recommended Next Actions */}
                          <div className="pcf-nextsteps-card">
                            <div className="pcf-nextsteps-card-header">
                              <div className="pcf-nextsteps-step-indicator">
                                <span className="pcf-nextsteps-step-number">2</span>
                              </div>
                              <div className="pcf-nextsteps-card-title">
                                Recommended Next Actions
                              </div>
                            </div>
                            <div className="pcf-nextsteps-card-content">
                              {(actions.length > 0 || customAction) ? (
                                <>
                                  {actions.length > 0 && (
                                    <ul className="pcf-transfer-details-list">
                                      {actions.map((id) => {
                                        const opt = recommendedOptions.find(o => o.id === id);
                                        return (
                                          <li key={id} className="pcf-transfer-details-list-item">
                                            {opt?.label || id}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                  {customAction && (
                                    <p className="pcf-transfer-details-text" style={{ marginTop: actions.length > 0 ? "8px" : 0 }}>
                                      {customAction}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="pcf-transfer-details-empty">None provided</p>
                              )}
                            </div>
                          </div>

                          {/* Unresolved Concerns */}
                          <div className="pcf-nextsteps-card">
                            <div className="pcf-nextsteps-card-header">
                              <div className="pcf-nextsteps-step-indicator">
                                <span className="pcf-nextsteps-step-number">3</span>
                              </div>
                              <div className="pcf-nextsteps-card-title">
                                Unresolved Concerns
                              </div>
                            </div>
                            <div className="pcf-nextsteps-card-content">
                              {(checklist.length > 0 || additionalConcerns) ? (
                                <>
                                  {checklist.length > 0 && (
                                    <ul className="pcf-transfer-details-list">
                                      {checklist.map((id) => {
                                        const opt = unresolvedOptions.find(o => o.id === id);
                                        return (
                                          <li key={id} className="pcf-transfer-details-list-item">
                                            {opt?.label || id}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                  {additionalConcerns && (
                                    <p className="pcf-transfer-details-text" style={{ marginTop: checklist.length > 0 ? "8px" : 0 }}>
                                      {additionalConcerns}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="pcf-transfer-details-empty">None noted</p>
                              )}
                            </div>
                          </div>

                          {/* Instructions for Next Peer Educator */}
                          <div className="pcf-nextsteps-card">
                            <div className="pcf-nextsteps-card-header">
                              <div className="pcf-nextsteps-step-indicator">
                                <span className="pcf-nextsteps-step-number">4</span>
                              </div>
                              <div className="pcf-nextsteps-card-title">
                                Instructions for Next Peer Educator
                              </div>
                            </div>
                            <div className="pcf-nextsteps-card-content">
                              {tf.instructionsNextPeer ? (
                                <p className="pcf-transfer-details-text">{tf.instructionsNextPeer}</p>
                              ) : (
                                <p className="pcf-transfer-details-empty">Not provided</p>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  /* ── TRANSFER FORM (new transfer) ─────────────────── */
                  <div className="pcf-nextsteps-form">
                    <div className="pcf-nextsteps-header">
                      <div className="pcf-nextsteps-ticket">
                        <span className="pcf-nextsteps-ticket-number">Ticket #{ticketNumber || "TKT-XXXX"}</span>
                        <span className="pcf-nextsteps-status">
                          <span className="pcf-nextsteps-dot"></span>
                          {chatStatus === "active" ? "Active Session" : "Resolved"}
                        </span>
                      </div>
                      <div className="pcf-nextsteps-progress">
                        <div className="pcf-nextsteps-progress-text">{completed}/{total} sections completed</div>
                        <div className="pcf-nextsteps-progress-bar">
                          <div
                            className="pcf-nextsteps-progress-fill"
                            style={{ width: `${(completed / total) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="pcf-nextsteps-actions">
                        <button
                          className="pcf-nextsteps-btn pcf-nextsteps-btn-preview"
                          onClick={() => setShowPreviewModal(true)}
                        >
                          Preview
                        </button>
                        <button
                          className="pcf-nextsteps-btn pcf-nextsteps-btn-save"
                          onClick={() => handleSaveNextSteps()}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Transferring...' : 'Transfer'}
                        </button>
                      </div>
                      {saveSuccess && (
                        <div className="pcf-nextsteps-success">
                          ✅ Transferred successfully!
                        </div>
                      )}
                    </div>

                    {/* 1. Conversation Context (Required) */}
                    <div className="pcf-nextsteps-card">
                      <div className="pcf-nextsteps-card-header">
                        <div className="pcf-nextsteps-step-indicator">
                          <span className="pcf-nextsteps-step-number">1</span>
                        </div>
                        <div className="pcf-nextsteps-card-title">
                          Conversation Context
                          <span className="pcf-nextsteps-badge pcf-nextsteps-required">Required</span>
                        </div>
                      </div>
                      <div className="pcf-nextsteps-card-content">
                        <textarea
                          className="pcf-nextsteps-textarea"
                          placeholder="Summarize the user's concern, important discussion points, emotional state, and any context the next peer educator should know."
                          value={nextSteps.context}
                          onChange={(e) => setNextSteps({ ...nextSteps, context: e.target.value })}
                          rows={4}
                        />
                      </div>
                    </div>

                    {/* 2. Recommended Next Actions (Required) */}
                    <div className="pcf-nextsteps-card">
                      <div className="pcf-nextsteps-card-header">
                        <div className="pcf-nextsteps-step-indicator">
                          <span className="pcf-nextsteps-step-number">2</span>
                        </div>
                        <div className="pcf-nextsteps-card-title">
                          Recommended Next Actions
                          <span className="pcf-nextsteps-badge pcf-nextsteps-required">Required</span>
                        </div>
                      </div>
                      <div className="pcf-nextsteps-card-content">
                        <div className="pcf-nextsteps-action-cards">
                          {recommendedOptions.map(option => (
                            <button
                              key={option.id}
                              className={`pcf-nextsteps-action-card ${nextSteps.recommendedActions.includes(option.id) ? 'active' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                const newActions = nextSteps.recommendedActions.includes(option.id)
                                  ? nextSteps.recommendedActions.filter(id => id !== option.id)
                                  : [...nextSteps.recommendedActions, option.id];
                                setNextSteps({ ...nextSteps, recommendedActions: newActions });
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        {(nextSteps.recommendedActions.includes('custom') || nextSteps.customRecommendation) && (
                          <div className="pcf-nextsteps-textarea-container">
                            <textarea
                              className="pcf-nextsteps-textarea"
                              placeholder="Add custom recommendation..."
                              maxLength={200}
                              value={nextSteps.customRecommendation}
                              onChange={(e) => setNextSteps({ ...nextSteps, customRecommendation: e.target.value })}
                              rows={2}
                            />
                            <div className="pcf-nextsteps-char-counter">
                              {nextSteps.customRecommendation.length}/200
                            </div>
                          </div>
                        )}
                    </div>
                      </div>

                    {/* 3. Unresolved Concerns (Optional) */}
                    <div className="pcf-nextsteps-card">
                      <div className="pcf-nextsteps-card-header">
                        <div className="pcf-nextsteps-step-indicator">
                          <span className="pcf-nextsteps-step-number">3</span>
                        </div>
                        <div className="pcf-nextsteps-card-title">
                          Unresolved Concerns
                          <span className="pcf-nextsteps-badge pcf-nextsteps-optional">Optional</span>
                        </div>
                      </div>
                      <div className="pcf-nextsteps-card-content">
                        <div className="pcf-nextsteps-checklist">
                          {unresolvedOptions.map(option => (
                            <label key={option.id} className="pcf-nextsteps-checklist-item">
                              <input
                                type="checkbox"
                                checked={nextSteps.unresolvedChecklist.includes(option.id)}
                                onChange={(e) => {
                                  const newChecklist = e.target.checked
                                    ? [...nextSteps.unresolvedChecklist, option.id]
                                    : nextSteps.unresolvedChecklist.filter(id => id !== option.id);
                                  setNextSteps({ ...nextSteps, unresolvedChecklist: newChecklist });
                                }}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="pcf-nextsteps-textarea-container">
                          <textarea
                            className="pcf-nextsteps-textarea"
                            placeholder="Additional unresolved concerns..."
                            maxLength={200}
                            value={nextSteps.unresolvedAdditional}
                            onChange={(e) => setNextSteps({ ...nextSteps, unresolvedAdditional: e.target.value })}
                            rows={2}
                          />
                          <div className="pcf-nextsteps-char-counter">
                            {nextSteps.unresolvedAdditional.length}/200
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 4. Instructions for Next Peer Educator (Optional) */}
                    <div className="pcf-nextsteps-card">
                      <div className="pcf-nextsteps-card-header">
                        <div className="pcf-nextsteps-step-indicator">
                          <span className="pcf-nextsteps-step-number">4</span>
                        </div>
                        <div className="pcf-nextsteps-card-title">
                          Instructions for Next Peer Educator
                          <span className="pcf-nextsteps-badge pcf-nextsteps-optional">Optional</span>
                        </div>
                      </div>
                      <div className="pcf-nextsteps-card-content">
                        <textarea
                          className="pcf-nextsteps-textarea"
                          placeholder="User responds better to…
Avoid discussing…
Follow up on…
Language preference…"
                          value={nextSteps.instructions}
                          onChange={(e) => setNextSteps({ ...nextSteps, instructions: e.target.value })}
                          rows={5}
                        />
                      </div>
                    </div>
                  </div>
            )}
              </div>
                )}
          </div>
        </div>
      </div>
      )}

      {/* Resource Viewer Modal */}
      {showResourceModal && selectedResource && (
        <div className="pcf-modal-overlay" onClick={() => setShowResourceModal(false)}>
          <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-header">
              <button className="pcf-modal-back-btn" onClick={() => setShowResourceModal(false)}>
                <FiArrowLeft />
              </button>
              <button className="pcf-modal-close" onClick={() => setShowResourceModal(false)}><FiX /></button>
            </div>
            <div className="pcf-modal-body">
              <div className="article-content">
                <div className="article-header">
                  <h1 className="article-title">{selectedResource.title}</h1>
                  <div className="article-meta">
                    <span className="article-meta-item">
                      <FiCalendar />
                      {selectedResource.createdAt ? (
                        selectedResource.createdAt.toDate ? 
                          selectedResource.createdAt.toDate().toLocaleDateString() : 
                          new Date(selectedResource.createdAt).toLocaleDateString()
                      ) : 'N/A'}
                    </span>
                    <span className="article-meta-item">
                      <FiFolder />
                      {selectedResource.category}
                    </span>
                  </div>
                </div>

                <div className="article-preview-container">
                  {!selectedResource.fileUrl ? (
                    <div className="article-placeholder">
                      <FiFile size={64} />
                      <p>No file available for preview</p>
                    </div>
                  ) : (
                    <>
                      {/* PDF Viewer */}
                      {selectedResource.type.toLowerCase() === 'pdf' && (
                        <div className="pdf-viewer-container">
                          <div className="pdf-viewer-controls">
                            <button 
                              onClick={prevPage}
                              disabled={pdfPageNumber <= 1}
                              className="pdf-viewer-btn"
                            >
                              Previous
                            </button>
                            <span className="pdf-viewer-page">
                              Page {pdfPageNumber} of {pdfNumPages}
                            </span>
                            <button 
                              onClick={nextPage}
                              disabled={pdfPageNumber >= pdfNumPages}
                              className="pdf-viewer-btn"
                            >
                              Next
                            </button>
                            <a 
                              href={selectedResource.fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="pdf-viewer-download"
                            >
                              <FiDownload /> Download
                            </a>
                          </div>
                          <div className="pdf-viewer-content">
                            <Document 
                              file={selectedResource.fileUrl} 
                              onLoadSuccess={onDocumentLoadSuccess}
                              loading={<div className="pdf-viewer-loading">Loading PDF...</div>}
                              error={<div className="pdf-viewer-error">Failed to load PDF</div>}
                            >
                              <Page 
                                pageNumber={pdfPageNumber} 
                                width={650}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                className="pdf-viewer-page"
                              />
                            </Document>
                          </div>
                        </div>
                      )}

                      {/* Image Viewer */}
                      {selectedResource.type.toLowerCase() === 'image' && (
                        <div className="image-viewer-container">
                          <div className="image-viewer-controls">
                            <a 
                              href={selectedResource.fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="pdf-viewer-download"
                            >
                              <FiDownload /> Download
                            </a>
                          </div>
                          <div className="image-viewer-content">
                            <img 
                              src={selectedResource.fileUrl} 
                              alt={selectedResource.title} 
                              className="image-viewer-img"
                            />
                          </div>
                        </div>
                      )}

                      {/* DOC/DOCX Viewer - Download Only */}
                      {(selectedResource.type.toLowerCase() === 'doc' || selectedResource.type.toLowerCase() === 'document' || selectedResource.type.toLowerCase() === 'docx') && (
                        <div className="doc-viewer-container">
                          <FiFileText size={56} />
                          <p>Document preview not available</p>
                          <a 
                            href={selectedResource.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="pdf-viewer-download"
                          >
                            <FiDownload /> Download Document
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps Preview Modal */}
      {showPreviewModal && (
        <div className="pcf-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="pcf-modal pcf-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <FiFileText size={22} />
                </div>
                <h3>Transfer Form Preview</h3>
              </div>
              <button className="pcf-modal-close" onClick={() => setShowPreviewModal(false)}><FiX /></button>
            </div>
            <div className="pcf-modal-body">
              {/* Ticket Info */}
              <div className="pcf-preview-section">
                <div className="pcf-preview-ticket">
                  <span className="pcf-preview-ticket-number">Ticket #{ticketNumber || "TKT-XXXX"}</span>
                  <span className="pcf-preview-status">
                    <span className="pcf-preview-dot"></span>
                    {chatStatus === "active" ? "Active Session" : "Resolved"}
                  </span>
                </div>
              </div>

              {/* Conversation Context */}
              <div className="pcf-preview-section">
                <h4 className="pcf-preview-title">Conversation Context</h4>
                <div className="pcf-preview-content">
                  {nextSteps.context.trim() ? (
                    <p className="pcf-preview-text">{nextSteps.context}</p>
                  ) : (
                    <p className="pcf-preview-empty">No context provided</p>
                  )}
                </div>
              </div>

              {/* Recommended Next Actions */}
              <div className="pcf-preview-section">
                <h4 className="pcf-preview-title">Recommended Next Actions</h4>
                <div className="pcf-preview-content">
                  {nextSteps.recommendedActions.length > 0 || nextSteps.customRecommendation.trim() ? (
                    <>
                      {nextSteps.recommendedActions.length > 0 && (
                        <ul className="pcf-preview-list">
                          {nextSteps.recommendedActions.map(id => {
                            const option = recommendedOptions.find(o => o.id === id);
                            return <li key={id}>{option?.label}</li>;
                          })}
                        </ul>
                      )}
                      {nextSteps.customRecommendation.trim() && (
                        <p className="pcf-preview-text">{nextSteps.customRecommendation}</p>
                      )}
                    </>
                  ) : (
                    <p className="pcf-preview-empty">No recommendations provided</p>
                  )}
                </div>
              </div>

              {/* Unresolved Concerns */}
              <div className="pcf-preview-section">
                <h4 className="pcf-preview-title">Unresolved Concerns</h4>
                <div className="pcf-preview-content">
                  {nextSteps.unresolvedChecklist.length > 0 || nextSteps.unresolvedAdditional.trim() ? (
                    <>
                      {nextSteps.unresolvedChecklist.length > 0 && (
                        <ul className="pcf-preview-list">
                          {nextSteps.unresolvedChecklist.map(id => {
                            const option = unresolvedOptions.find(o => o.id === id);
                            return <li key={id}>{option?.label}</li>;
                          })}
                        </ul>
                      )}
                      {nextSteps.unresolvedAdditional.trim() && (
                        <p className="pcf-preview-text">{nextSteps.unresolvedAdditional}</p>
                      )}
                    </>
                  ) : (
                    <p className="pcf-preview-empty">No unresolved concerns</p>
                  )}
                </div>
              </div>

              {/* Instructions for Next Peer Educator */}
              <div className="pcf-preview-section">
                <h4 className="pcf-preview-title">Instructions for Next Peer Educator</h4>
                <div className="pcf-preview-content">
                  {nextSteps.instructions.trim() ? (
                    <p className="pcf-preview-text">{nextSteps.instructions}</p>
                  ) : (
                    <p className="pcf-preview-empty">No instructions provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="pcf-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="pcf-modal pcf-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-error-header">
              <h3 className="pcf-error-header-title">Missing Required Information</h3>
              <button className="pcf-error-close" onClick={() => setShowErrorModal(false)}>
                <FiX size={22} />
              </button>
            </div>
            <div className="pcf-error-content">
              <div className="pcf-error-icon-wrapper">
                <svg className="pcf-error-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                    stroke="currentColor" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h4 className="pcf-error-title">Missing Required Information</h4>
              <p className="pcf-error-message">{errorMessage}</p>
              <button 
                className="pcf-error-btn"
                onClick={() => setShowErrorModal(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalate Modal */}
      {showEscalateModal && (
        <div className="pcf-modal-overlay" onClick={handleEscalateCancel}>
          <div className="pcf-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-header">
              <div>
                <h3>Escalate Chat to Medical Professional</h3>
                <p className="pcf-modal-subtitle" style={{ color: '#ffffff' }}>This conversation will be forwarded to available Medical Professionals for further assistance. Please provide the reason for escalation.</p>
              </div>
              <button className="pcf-modal-close" onClick={handleEscalateCancel}>
                <FiX />
              </button>
            </div>
            <div className="pcf-modal-body">

              {/* Topic of Inquiry */}
              <div className="pcf-form-group">
                <label className="pcf-form-label">1. Topic of Inquiry <span className="pcf-required">*</span></label>
                <p className="pcf-form-sublabel">What is the user's concern about?</p>
                <select
                  className={`pcf-form-select ${escalationErrors.topic ? "pcf-form-error" : ""}`}
                  value={escalationForm.topic}
                  onChange={(e) => handleEscalationFormChange("topic", e.target.value)}
                >
                  <option value="">Select a topic...</option>
                  <option value="HIV Awareness">HIV Awareness</option>
                  <option value="HIV Screening/Testing">HIV Screening/Testing</option>
                  <option value="Birth Control/Implant">Birth Control/Implant</option>
                  <option value="Contraception">Contraception</option>
                  <option value="Family Planning">Family Planning</option>
                  <option value="Gender-Based Violence">Gender-Based Violence</option>
                  <option value="Menstrual Health & Hygiene">Menstrual Health & Hygiene</option>
                </select>
                {escalationErrors.topic && <p className="pcf-error-message">Please select a topic of inquiry.</p>}
              </div>

              {/* Escalation Reason */}
              <div className="pcf-form-group">
                <label className="pcf-form-label">2. Escalation Reason <span className="pcf-required">*</span></label>
                <p className="pcf-form-sublabel">Why are you escalating this chat?</p>
                <div className={`pcf-radio-group ${escalationErrors.reason ? "pcf-form-error" : ""}`}>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Medical advice needed" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Medical advice needed"
                      checked={escalationForm.reason === "Medical advice needed"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Medical advice needed</span>
                    <p className="pcf-radio-description">User requires professional health guidance beyond peer support</p>
                  </label>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Possible symptoms or health concern" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Possible symptoms or health concern"
                      checked={escalationForm.reason === "Possible symptoms or health concern"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Possible symptoms or health concern</span>
                    <p className="pcf-radio-description">User mentioned symptoms, discomfort, or a possible condition</p>
                  </label>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Medication/treatment-related question" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Medication/treatment-related question"
                      checked={escalationForm.reason === "Medication/treatment-related question"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Medication/treatment-related question</span>
                    <p className="pcf-radio-description">User asked about medicine, treatment, or clinical recommendations</p>
                  </label>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Urgent concern" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Urgent concern"
                      checked={escalationForm.reason === "Urgent concern"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Urgent concern</span>
                    <p className="pcf-radio-description">User expressed a concern that may require immediate professional attention</p>
                  </label>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Sensitive/personal situation" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Sensitive/personal situation"
                      checked={escalationForm.reason === "Sensitive/personal situation"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Sensitive/personal situation</span>
                    <p className="pcf-radio-description">User needs professional support for a sensitive matter</p>
                  </label>
                  <label className={`pcf-radio-label ${escalationForm.reason === "Unable to provide sufficient information" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="escalationReason"
                      value="Unable to provide sufficient information"
                      checked={escalationForm.reason === "Unable to provide sufficient information"}
                      onChange={(e) => handleEscalationFormChange("reason", e.target.value)}
                    />
                    <span>Unable to provide sufficient information</span>
                    <p className="pcf-radio-description">Question requires expertise beyond peer educator scope</p>
                  </label>
                </div>
                {escalationErrors.reason && <p className="pcf-error-message">Please select an escalation reason.</p>}
              </div>

              {/* Escalation Notes */}
              <div className="pcf-form-group">
                <label className="pcf-form-label">3. Escalation Notes <span className="pcf-required">*</span></label>
                <p className="pcf-form-sublabel">Additional notes for Medical Professional</p>
                <div className="pcf-textarea-wrapper">
                  <textarea
                    className={`pcf-form-textarea ${escalationErrors.notes ? "pcf-form-error" : ""}`}
                    placeholder="Briefly explain the user's concern and provide any important details that may help the Medical Professional understand the situation."
                    value={escalationForm.notes}
                    onChange={(e) => handleEscalationFormChange("notes", e.target.value)}
                    rows={4}
                    maxLength={300}
                  />
                  <span className="pcf-char-counter">{escalationForm.notes.length}/300</span>
                </div>
                {escalationErrors.notes && <p className="pcf-error-message">Please provide escalation notes.</p>}
              </div>
            </div>
            <div className="pcf-modal-footer">
              <button className="pcf-modal-btn pcf-modal-btn-cancel" onClick={handleEscalateCancel}>
                Cancel
              </button>
              <button className="pcf-modal-btn pcf-modal-btn-submit" onClick={handleEscalateSubmit}>
                Escalate Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profanity Modal */}
      <ProfanityModal 
        isOpen={showProfanityModal} 
        onClose={() => setShowProfanityModal(false)} 
      />
    </div>
  );
};

export default PeerChatForm;
